import React, { useLayoutEffect, useRef } from "react";

/**
 * AutoFitLine — renders `children` as a single un-wrapped line and
 * proportionally shrinks the font-size (down to `min`) until the content
 * fits inside the container. Used in the Vakalatnama contact box so the
 * E-mail and Mobile Number always sit on ONE single line, even when the
 * email address is long. Falls back to `max` when there is enough room.
 *
 * Works in both the preview modal and the persistent .pdf-render-host
 * because useLayoutEffect runs synchronously after mount, before paint
 * and before html2canvas captures the frame.
 */
export default function AutoFitLine({
  children,
  min = 9,
  max = 14,
  className = "",
  gap = 24,
  testId,
}) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);

  const measure = () => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    // Reset to max first so we can measure the natural width.
    inner.style.fontSize = `${max}px`;
    const availableW = outer.clientWidth;
    const naturalW = inner.scrollWidth;
    if (availableW <= 0 || naturalW <= 0) return;
    if (naturalW <= availableW) {
      inner.style.fontSize = `${max}px`;
    } else {
      const factor = availableW / naturalW;
      const next = Math.max(min, Math.floor(max * factor * 10) / 10);
      inner.style.fontSize = `${next}px`;
    }
  };

  useLayoutEffect(() => {
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined" && outerRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(outerRef.current);
    }
    return () => {
      if (ro) ro.disconnect();
    };
  }, [children, min, max, gap]);

  return (
    <div
      ref={outerRef}
      className={`adv-contact-line ${className}`}
      data-testid={testId}
      style={{ width: "100%", overflow: "visible", paddingBottom: 4 }}
    >
      <div
        ref={innerRef}
        className="adv-contact-line-inner"
        style={{
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "baseline",
          fontSize: `${max}px`,
          columnGap: `${gap}px`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

